import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";
import { EASE } from "./Reveal";
import { scrollToId, PHONE_TEL, PHONE_DISPLAY } from "../lib";

const LINKS = [
    { id: "legacy", label: "Legacy" },
    { id: "products", label: "Products" },
    { id: "capabilities", label: "Capabilities" },
    { id: "projects", label: "Where We Innovate" },
    { id: "frontlines", label: "Frontlines" },
    { id: "contact", label: "Contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 250 : 0);
    };

    return (
        <>
            <motion.header
                initial={{ y: -70, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-line bg-ink/80 backdrop-blur-xl"
                        : "bg-transparent"
                }`}
                data-testid="site-header"
            >
                <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                    <button
                        data-testid="header-brand-logo"
                        onClick={() => window.__lenis?.scrollTo(0)}
                        className="flex shrink-0 items-center gap-3 lg:mr-5"
                    >
                        <LogoMark size={34} />
                        <Wordmark compact />
                    </button>

                    <nav className="hidden items-center gap-4 lg:flex xl:gap-6">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="group relative whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300 transition-colors hover:text-white"
                            >
                                {l.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
                            </button>
                        ))}
                    </nav>

                    <div className="hidden items-center gap-3 lg:flex">
                        <a
                            data-testid="header-phone-cta"
                            href={PHONE_TEL}
                            className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-line px-4 py-2 font-mono text-xs tracking-wider text-slate-200 transition-colors hover:border-accent/50 hover:text-accent xl:flex"
                        >
                            <Phone className="h-3.5 w-3.5" />
                            {PHONE_DISPLAY}
                        </a>
                        <button
                            data-testid="header-enquire-button"
                            onClick={() => go("contact")}
                            className="rounded-full bg-accent px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink transition-transform duration-300 hover:scale-105"
                        >
                            Enquire
                        </button>
                    </div>

                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(true)}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-white lg:hidden"
                        aria-label="Open menu"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[80] flex flex-col bg-ink/95 backdrop-blur-2xl lg:hidden"
                        data-testid="mobile-menu-overlay"
                    >
                        <div className="flex h-[72px] items-center justify-between px-4 sm:px-6">
                            <div className="flex items-center gap-3">
                                <LogoMark size={34} />
                                <Wordmark compact />
                            </div>
                            <button
                                data-testid="mobile-menu-close-button"
                                onClick={() => setOpen(false)}
                                className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-white"
                                aria-label="Close menu"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: EASE }}
                                    onClick={() => go(l.id)}
                                    className="border-b border-line py-5 text-left font-display text-3xl font-bold uppercase tracking-tight text-white"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                        </nav>
                        <div className="px-8 pb-10">
                            <a
                                href={PHONE_TEL}
                                className="flex items-center gap-3 font-mono text-sm tracking-wider text-accent"
                                data-testid="mobile-menu-phone-link"
                            >
                                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
